<template>
  <div>
    <page-header
      title="用户与角色"
      :crumbs="['系统设置', '用户与角色']"
      :subtitle="`共 ${users.length} 个用户`"
    >
      <template #actions>
        <button v-if="hasPermission('sys:user:add')" class="btn btn-primary" @click="showAddDialog">
          新增用户
        </button>
      </template>
    </page-header>

    <DataTable
      :columns="columns"
      :data="users"
      :total="0"
      row-key="id"
      :ops-width="190"
      empty-text="暂无用户"
      @refresh="getUsers"
    >
      <template #toolbar-left>
        <span class="dt-hint">共 {{ users.length }} 个用户</span>
      </template>

      <template #cell-status="{ row }">
        <span class="tag" :class="row.status === 1 ? 'tag-success' : 'tag-neutral'">
          {{ row.status === 1 ? '正常' : '禁用' }}
        </span>
      </template>

      <template #ops="{ row }">
        <button v-if="hasPermission('sys:user:edit')" class="dt-op" @click="showEditDialog(row)">编辑</button>
        <span v-if="hasPermission('sys:user:edit')" class="dt-sep">·</span>
        <button v-if="hasPermission('sys:user:assignRole')" class="dt-op" @click="showRoleDialog(row)">分配角色</button>
        <span v-if="hasPermission('sys:user:assignRole')" class="dt-sep">·</span>
        <button v-if="hasPermission('sys:user:delete')" class="dt-op is-danger" @click="deleteUser(row.id)">删除</button>
      </template>
    </DataTable>

    <!-- 新增 / 编辑用户 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="500px">
      <el-form :model="form" label-position="top">
        <el-form-item label="用户名">
          <el-input v-model="form.username" :disabled="form.id !== null" placeholder="请输入用户名" />
        </el-form-item>
        <el-form-item label="真实姓名">
          <el-input v-model="form.realName" placeholder="请输入真实姓名" />
        </el-form-item>
        <el-form-item v-if="!form.id" label="密码">
          <el-input v-model="form.password" type="password" show-password placeholder="请输入密码" />
        </el-form-item>
        <el-form-item label="手机号">
          <el-input v-model="form.phone" placeholder="请输入手机号" />
        </el-form-item>
        <el-form-item label="邮箱">
          <el-input v-model="form.email" placeholder="请输入邮箱" />
        </el-form-item>
        <el-form-item label="状态">
          <el-switch v-model="form.status" :active-value="1" :inactive-value="0" active-text="正常" inactive-text="禁用" />
        </el-form-item>
      </el-form>
      <template #footer>
        <button class="btn" @click="dialogVisible = false">取消</button>
        <button class="btn btn-primary" @click="saveUser">保存</button>
      </template>
    </el-dialog>

    <!-- 分配角色 -->
    <el-dialog v-model="roleDialogVisible" title="分配角色" width="500px">
      <el-form label-position="top">
        <el-form-item label="角色">
          <el-select v-model="selectedRoleIds" multiple collapse-tags placeholder="请选择角色" style="width: 100%">
            <el-option v-for="role in allRoles" :key="role.id" :label="role.roleName" :value="role.id" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <button class="btn" @click="roleDialogVisible = false">取消</button>
        <button class="btn btn-primary" @click="saveRoles">保存</button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { onMounted, reactive, toRefs, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import PageHeader from '@/components/PageHeader.vue'
import DataTable from '@/components/DataTable.vue'
import userService from '@/services/userService'
import roleService from '@/services/roleService'
import permissionService from '@/services/permissionService'
import store from '@/store'

export default {
  name: 'UserManagement',
  components: { PageHeader, DataTable },
  setup() {
    const state = reactive({
      users: [],
      allRoles: [],
      dialogVisible: false,
      roleDialogVisible: false,
      dialogTitle: '新增用户',
      form: { id: null, username: '', realName: '', password: '', phone: '', email: '', status: 1 },
      currentUserId: null,
      selectedRoleIds: []
    })

    const columns = [
      { prop: 'id', label: 'ID', width: 70 },
      { prop: 'username', label: '用户名', minWidth: 130 },
      { prop: 'realName', label: '真实姓名', minWidth: 120 },
      { prop: 'phone', label: '手机号', minWidth: 140 },
      { prop: 'email', label: '邮箱', minWidth: 200 },
      { prop: 'status', label: '状态', width: 90 }
    ]

    const permissionCodes = computed(() => {
      const permissions = store.state.permissions
      return permissions ? Object.values(permissions).map(p => p.permissionCode) : []
    })

    const hasPermission = (code) => permissionCodes.value.includes(code)

    const getUsers = async () => {
      try {
        const response = await userService.getUsersByTenantId(store.state.tenantId)
        state.users = response?.data || []
      } catch {
        ElMessage.error('获取用户列表失败')
      }
    }

    const getRoles = async () => {
      try {
        const response = await roleService.getRolesByTenantId(store.state.tenantId)
        state.allRoles = response?.data || []
      } catch {
        ElMessage.error('获取角色列表失败')
      }
    }

    const showAddDialog = () => {
      state.dialogTitle = '新增用户'
      state.form = { id: null, username: '', realName: '', password: '', phone: '', email: '', status: 1 }
      state.dialogVisible = true
    }

    const showEditDialog = (user) => {
      state.dialogTitle = '编辑用户'
      state.form = { ...user, password: '' }
      state.dialogVisible = true
    }

    const showRoleDialog = async (user) => {
      state.currentUserId = user.id
      state.roleDialogVisible = true
      await getRoles()
      try {
        const response = await permissionService.getRolesByUserId(user.id)
        state.selectedRoleIds = (response?.data || []).map(role => role.id)
      } catch {
        ElMessage.error('获取用户角色失败')
      }
    }

    const saveUser = async () => {
      try {
        if (state.form.id) {
          await userService.updateUser(state.form)
          ElMessage.success('更新成功')
        } else {
          await userService.addUser(state.form)
          ElMessage.success('新增成功')
        }
        state.dialogVisible = false
        getUsers()
      } catch (error) {
        ElMessage.error(error?.message || '操作失败')
      }
    }

    const saveRoles = async () => {
      try {
        await userService.assignRoles(state.currentUserId, state.selectedRoleIds)
        ElMessage.success('角色分配成功')
        state.roleDialogVisible = false
      } catch (error) {
        ElMessage.error(error?.message || '分配失败')
      }
    }

    const deleteUser = async (id) => {
      try {
        await ElMessageBox.confirm('确定要删除该用户吗？', '提示', {
          confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning'
        })
      } catch {
        return
      }
      try {
        await userService.deleteUser(id)
        ElMessage.success('删除成功')
        getUsers()
      } catch (error) {
        ElMessage.error(error?.message || '删除失败')
      }
    }

    onMounted(getUsers)

    return {
      ...toRefs(state), columns,
      showAddDialog, showEditDialog, showRoleDialog,
      saveUser, saveRoles, deleteUser, getUsers, hasPermission
    }
  }
}
</script>

<style scoped>
.tag {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  padding: 1px 6px;
  border-radius: var(--radius-tag);
  white-space: nowrap;
}

.tag-success { background: var(--success-bg); color: var(--success-text); }
.tag-neutral { background: var(--bg-page);    color: var(--text-secondary); }

.dt-hint {
  font-size: 12px;
  color: var(--text-secondary);
}

.btn {
  padding: 6px 14px;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  background: var(--bg-card);
  color: var(--text-secondary);
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;
}

.btn:hover {
  border-color: var(--brand-500);
  color: var(--brand-500);
}

.btn-primary {
  background: var(--brand-500);
  border-color: var(--brand-500);
  color: #fff;
}

.btn-primary:hover {
  background: var(--brand-600);
  border-color: var(--brand-600);
  color: #fff;
}
</style>
